import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Reseta o scroll de um container para o topo a cada navegacao (mudanca de
 * `pathname`).
 *
 * Contexto: no shell autenticado (`(app)/_layout.tsx`) o container rolavel
 * NAO e o `window`, e sim o `<main data-testid="app-main">` interno
 * (`overflow-y-auto`). O React Router so reseta o scroll do documento — o
 * scroll de um container interno persiste entre rotas, deixando a nova tela
 * "rolada para baixo". Este hook corrige isso de forma explicita.
 *
 * Contrato:
 *  - So dispara quando o `pathname` MUDA — nao no mount inicial (a tela ja
 *    nasce no topo; rolar no mount seria trabalho desnecessario e poderia
 *    brigar com restauracao de scroll futura).
 *  - No-op seguro quando `ref.current` e `null` ou quando o elemento nao
 *    expoe `scrollTo` (ex: ambientes/elements sem a API).
 *
 * @param ref Ref para o elemento rolavel (ex: o `<main>` do layout).
 *
 * @example
 *   const mainRef = useRef<HTMLElement>(null);
 *   useScrollToTop(mainRef);
 *   return <main ref={mainRef} className="overflow-y-auto">...</main>;
 */
export function useScrollToTop(ref: RefObject<HTMLElement | null>): void {
  const { pathname } = useLocation();
  // Ignora o primeiro efeito (mount): so reagimos a TROCAS de pathname.
  const isFirst = useRef(true);

  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false;
      return;
    }
    const el = ref.current;
    if (el && typeof el.scrollTo === 'function') {
      el.scrollTo({ top: 0 });
    }
  }, [pathname, ref]);
}
